import { getHandwritingMap, putHandwriting } from '../lib/handwriting.ts';
import { normalizeAppData } from './migrate.ts';
import { useStore } from './store.ts';
import type { AppData } from './types.ts';

/**
 * JSON backup, in exactly the format the original app produced.
 *
 * This matters more than it looks: it is the escape hatch. A backup taken
 * from the old version must import here, and a backup taken here must still
 * open in the old version. So the sync envelope is stripped and handwriting
 * is re-inlined as data-URLs, which is where the old app expected it.
 */
const FILE_NAME = 'journal-de-classe-sauvegarde.json';

type LegacyRow = Record<string, unknown>;

/** Strip the fields the old format never had. */
function stripSyncFields<T extends object>(row: T): LegacyRow {
  const { updatedAt, deletedAt, dirty, ...rest } = row as T & {
    updatedAt?: unknown;
    deletedAt?: unknown;
    dirty?: unknown;
  };
  void updatedAt;
  void deletedAt;
  void dirty;
  return rest as LegacyRow;
}

/** Deleted rows are an implementation detail of sync; a backup omits them. */
function exportable<T extends { deletedAt: string | null }>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.deletedAt === null);
}

export async function buildBackup(data: AppData): Promise<string> {
  const observations = exportable(data.observations);

  // Re-inline the PNGs so the file stands alone, as it always did.
  const handwriting = await getHandwritingMap(
    observations.filter((o) => o.hasHandwriting).map((o) => o.id),
  );

  const payload = {
    settings: stripSyncFields(data.settings),
    students: exportable(data.students).map(stripSyncFields),
    tasks: exportable(data.tasks).map(stripSyncFields),
    postits: exportable(data.postits).map(stripSyncFields),
    lessons: exportable(data.lessons).map(stripSyncFields),
    observations: observations.map((o) => {
      const row = stripSyncFields(o);
      delete row['hasHandwriting'];
      delete row['handwritingPath'];
      return { ...row, handwriting: handwriting[o.id] ?? '' };
    }),
    evaluations: exportable(data.evaluations).map(stripSyncFields),
    meetings: exportable(data.meetings).map(stripSyncFields),
    skills: exportable(data.skills).map(stripSyncFields),
    weekTemplate: exportable(data.weekTemplate).map((e) => {
      const row = stripSyncFields(e);
      // The old format identified template rows by nulls in these two fields.
      return { ...row, id: null, weekStart: null };
    }),
  };

  return JSON.stringify(payload, null, 2);
}

/** Trigger a download of the current data. */
export async function exportData(): Promise<void> {
  const json = await buildBackup(useStore.getState());
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = FILE_NAME;
  // WebKit is more reliable when the link is part of the document, and the
  // object URL must survive until the browser has begun the download.
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Replace everything with the contents of a backup file.
 *
 * Throws on unparseable input so the caller can tell the user, rather than
 * silently emptying the journal.
 */
export async function importBackup(text: string): Promise<void> {
  const parsed: unknown = JSON.parse(text);
  const { data, handwriting } = normalizeAppData(parsed);

  await Promise.all(
    Object.entries(handwriting).map(([id, dataUrl]) => putHandwriting(id, dataUrl)),
  );

  useStore.getState().replaceAll(data);
}
